<template>
  <div class="customization-upload-image-wrapper">
    <div class="customization-upload-image-upload-title">{{ title }}</div>
    <CustomFileUpload></CustomFileUpload>
    <span class="tooltip-image-processing" v-show="loading">
      {{ imageProcessingText }}
      {{ percentProcessed ? `${percentProcessed}%` : '' }}
    </span>
    <span
      class="customization-upload-image-wrapper-message"
      v-show="isShowMessage"
      :class="messageClasses"
    >
      {{ messageFaceSwap }}
    </span>

    <input
      type="hidden"
      :name="`properties[${FACE_SWAP_IMAGE_PREFIX} ${title}]`"
      :value="imageFaceSwapUploaded"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, h, onMounted, ref, watch } from 'vue'
import FileUpload from '@/ui-kits/FileSelect.vue'
import Logger from '@/services/log'
import useCustomizationStore from '@/store/customization'
import {
  EVENT_CUSTOM_UPLOAD_IMAGE,
  CUSTOMIZATION_UPLOAD_IMAGE_WRAPPER_CLASS,
  SIB_FILE_SELECT_WRAPPER_CLASS,
  FACE_SWAP_IMAGE_PREFIX,
} from '@/utils'
import EventBus from '@/services/eventbus'
import { useProductCustomization } from './composables/ProductCustomization'

const customizationStore = useCustomizationStore()
const {
  convertImageLinkToFile,
  convertImageLinkToFileList,
  changeFileImageCustomily,
  startUpload,
  finishUpload,
  percentProcessed,
} = useProductCustomization()

const props = withDefaults(
  defineProps<{
    id: string
    inputFileCustomily: Element
    selectImageButtonCustomily: Element
    faceIndex: number
    title: string
    imageDefault: string
    optionSortId: string
    templateIdSelected: any
  }>(),
  {}
)

const messageFaceSwap = computed(() =>
  isSuccess.value ? successfullMessage : errorMessage.value
)
const messageClasses = computed(() =>
  isSuccess.value
    ? 'text-primary ml-xs'
    : `text-secondary ${CUSTOMIZATION_UPLOAD_IMAGE_WRAPPER_CLASS}-message--error`
)

const imageFaceSwapUploaded = computed(() => {
  const index = customizationStore.faceSwapped.findIndex(
    (item) =>
      item.optionSortId == props.optionSortId &&
      item.templateId == props.templateIdSelected
  )
  if (index > -1) {
    return customizationStore.faceSwapped[index][props.title] || ''
  }
  return ''
})

let loading = ref(false)
let isSuccess = ref(false)
let isShowMessage = ref(false)
const imageProcessingText = 'Processing'
const successfullMessage = 'Successful!'
let errorMessage = ref('Error!')
const sibFileNameFaceSwapImagePrefix = 'sib-file-name-face-swap-image'
let faceSwappedLink = ref('')
let faceImageUploadedLink = ref('')

onMounted(async () => {
  if (customizationStore.faceSwapped.length) {
    const faceSwappedItem = customizationStore.faceSwapped.find(
      (item) =>
        item.optionSortId == props.optionSortId &&
        item.templateId == props.templateIdSelected
    )
    if (faceSwappedItem) {
      faceSwappedLink.value = faceSwappedItem.sourceLink
      const fileName = `${sibFileNameFaceSwapImagePrefix}-${faceSwappedLink.value}`
      const imageFileList = await convertImageLinkToFileList(
        faceSwappedLink.value!,
        fileName
      )
      changeFileImageCustomily(imageFileList, props.inputFileCustomily)
    }
  }

  watch(
    () => loading.value,
    (value) => {
      EventBus.trigger(EVENT_CUSTOM_UPLOAD_IMAGE, value)
      handleClassListUploadButtons(value)
      if (value) startUpload()
      else finishUpload()
    }
  )
  watch(
    () => props.templateIdSelected,
    () => {
      isShowMessage.value = false
    }
  )
})

const CustomFileUpload = () => {
  let tag: any = FileUpload
  const propsCustomFileUpload: {
    [key: string]: any
  } = {}

  propsCustomFileUpload.id = props.id
  propsCustomFileUpload.loading = loading.value
  propsCustomFileUpload.onChange = onChange

  async function onChange(event: Event) {
    const input = event.target as HTMLInputElement
    if (!input.files) return
    const file = input.files[0]
    if (!file) return

    loading.value = true
    isSuccess.value = false
    isShowMessage.value = false

    const index = customizationStore.faceSwapped.findIndex(
      (item) =>
        item.optionSortId == props.optionSortId &&
        item.templateId == props.templateIdSelected
    )
    const sourceLink =
      index > -1
        ? customizationStore.faceSwapped[index].sourceLink
        : props.imageDefault
    const sourceFile = await convertImageLinkToFile(
      sourceLink,
      `face-swap-source-file-${sourceLink}`
    )

    const payload = {
      face_index: props.faceIndex,
      face_file: file,
      source_file: sourceFile as File,
    }
    customizationStore
      .faceSwap(payload)
      .then(async (response) => {
        if (response.success === false) {
          throw response
        }
        if (!response.image || !response.face_image) {
          throw { message: errorMessage.value }
        }

        faceImageUploadedLink.value = response.face_image
        faceSwappedLink.value = response.image

        const fileName = `${sibFileNameFaceSwapImagePrefix}-${faceSwappedLink.value}`
        const imageFileList = await convertImageLinkToFileList(
          faceSwappedLink.value,
          fileName
        )
        changeFileImageCustomily(imageFileList, props.inputFileCustomily)
        isSuccess.value = true
        saveFaceSwapped()
      })
      .catch((err) => {
        errorMessage.value = err.message ?? errorMessage.value
        isSuccess.value = false
        Logger.log('Error when face swap', err)
      })
      .finally(() => {
        if (isSuccess.value) {
          customilyUpLoadingHandler()
        } else {
          loading.value = false
          isShowMessage.value = true
        }
      })
    input.value = ''
  }

  return h(tag, propsCustomFileUpload)
}

function customilyUpLoadingHandler() {
  const timeout = setTimeout(() => {
    const isCustomilyUpLoading =
      props.selectImageButtonCustomily?.classList.contains('disabled')
    if (isCustomilyUpLoading) {
      customilyUpLoadingHandler()
    } else {
      loading.value = false
      isShowMessage.value = true
      clearTimeout(timeout)
    }
  }, 200)
}

function saveFaceSwapped() {
  const index = customizationStore.faceSwapped.findIndex(
    (item) =>
      item.optionSortId == props.optionSortId &&
      item.templateId == props.templateIdSelected
  )
  const faceSwappedItem = {
    optionSortId: props.optionSortId,
    templateId: props.templateIdSelected,
    sourceLink: faceSwappedLink.value,
    [props.title]: faceImageUploadedLink.value,
  }
  if (index > -1) {
    customizationStore.faceSwapped.splice(index, 1, {
      ...customizationStore.faceSwapped[index],
      ...faceSwappedItem,
    })
  } else {
    customizationStore.faceSwapped.push(faceSwappedItem)
  }
}

function handleClassListUploadButtons(loading: boolean) {
  const uploadFileFaceSwapButtons = document.querySelectorAll(
    `.${CUSTOMIZATION_UPLOAD_IMAGE_WRAPPER_CLASS} .${SIB_FILE_SELECT_WRAPPER_CLASS}`
  )

  // cancel event select file while processing face swap image
  uploadFileFaceSwapButtons.forEach((button) => {
    if (loading) {
      button.classList.add(`${SIB_FILE_SELECT_WRAPPER_CLASS}--loading`)
    } else {
      button.classList.remove(`${SIB_FILE_SELECT_WRAPPER_CLASS}--loading`)
    }
  })
}
</script>
